// Sort presentation only. Callers still filter menus using the access grants.
export function orderMenuItems(items, menuOrder = []) {
    const ranks = new Map();
    menuOrder.forEach((key, index) => { if (!ranks.has(key)) ranks.set(key, index); });
    const walk = nodes => nodes.map((item, index) => {
        const children = item.children ? walk(item.children) : [];
        const rank = Math.min(ranks.get(item.key) ?? Infinity, ...children.map(child => child.rank));
        return { item: item.children ? { ...item, children: children.map(child => child.item) } : item, rank, index };
    }).sort((a, b) => a.rank - b.rank || a.index - b.index);
    return walk(items).map(node => node.item);
}

// Build navigation from the published canonical tree. Only registered sidebar
// entries become links; detail routes and authorization references stay hidden.
export function buildMenuItems(items, menuTree, menuOrder = []) {
    if (!Array.isArray(menuTree) || !menuTree.length) return orderMenuItems(items, menuOrder);
    const templates = new Map(), entries = new Map(), emitted = new Set();
    const collect = nodes => nodes.forEach(item => {
        templates.set(item.key, item);
        if (item.children) collect(item.children);
        else entries.set(item.key, item);
    });
    collect(items);
    const walk = nodes => nodes.flatMap(node => {
        const links = (node.entries || []).flatMap(key => {
            if (!entries.has(key) || emitted.has(key)) return [];
            emitted.add(key);
            return [{ ...entries.get(key) }];
        });
        const children = [...links, ...walk(node.children || [])];
        if (!children.length) return [];
        // Resource nodes own links. Directory nodes provide their actual parent.
        if (node.entries?.length && !node.children?.length) return links;
        // Keep the existing single-page wrappers compact until another page is
        // moved into them (e.g. Platform News then becomes an expandable group).
        if (children.length === 1 && !children[0].children && node.name === children[0].label) return children;
        const template = templates.get(node.key);
        return [{ key: `permission-directory:${node.key}`, label: node.name, title: node.name,
            icon: template?.icon || children[0]?.icon, children }];
    });
    return walk(menuTree);
}

// Directory visibility is derived recursively from allowed links, at any depth.
export function filterMenuItems(items, allowedEntries) {
    const allowed = new Set(allowedEntries);
    return items.flatMap(item => {
        if (!item.children) return allowed.has(item.key) ? [item] : [];
        const children = filterMenuItems(item.children, allowedEntries);
        return children.length ? [{ ...item, children }] : [];
    });
}
